import type { JSX } from 'react';
import { Progress } from '@heroui/react';

interface TaskProgressBarProps {
  completedTasks?: number;
  totalTasks?: number;
}

/**
 * Always renders the task-progress line so every project card carries a
 * consistent bottom row: `${completedTasks}/${totalTasks} tasks` plus the
 * matching percentage, including the zero-task case (`0/0 tasks`, 0%). It
 * never substitutes the word "Complete" for the numeric fraction — the
 * count is the single, consistent representation across all cards.
 *
 * Pattern: docs/design-patterns/frontend/ui-component-states.md (consistent state model)
 * Pattern: docs/design-patterns/frontend/ux-writing.md (one term for one concept)
 */
export function TaskProgressBar({
  completedTasks = 0,
  totalTasks = 0,
}: TaskProgressBarProps): JSX.Element {
  const percentage = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

  return (
    <div className="flex items-center gap-2 text-xs text-foreground-600">
      <span className="shrink-0 whitespace-nowrap">{`${completedTasks}/${totalTasks} tasks`}</span>
      <Progress
        size="sm"
        value={percentage}
        color="primary"
        className="flex-1"
        aria-label="Task progress"
      />
      <span className="shrink-0">{percentage}%</span>
    </div>
  );
}
